/**
 * NestEngine v2 — Polygonal nesting engine
 * Shared by DTF nesting & Patronnage tools
 * Features: contour extraction, fine rotations, GA + simulated annealing
 */
(function(global){
'use strict';

var NE = {};

// ==================== CONTOUR EXTRACTION ====================

NE.extractContour = function(img, opts) {
  opts = opts || {};
  var aT = opts.alphaThreshold || 30;

  var iw = img.naturalWidth || img.width;
  var ih = img.naturalHeight || img.height;
  if (iw < 2 || ih < 2) return _rect();

  // Use full resolution for accuracy (cap at 800px to stay fast)
  var maxR = opts.maxResolution || 800;
  var sc = Math.min(1, maxR / Math.max(iw, ih));
  var w = Math.max(4, Math.round(iw * sc));
  var h = Math.max(4, Math.round(ih * sc));

  var cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  var cx = cv.getContext('2d');
  cx.drawImage(img, 0, 0, w, h);
  var imgData = cx.getImageData(0, 0, w, h);
  var d = imgData.data;

  // Detect alpha vs background
  var hasA = false;
  for (var i = 3; i < d.length; i += 4) { if (d[i] < 250) { hasA = true; break; } }

  // Create a clean binary image for tracing: white shape on black background
  var traceCanvas = document.createElement('canvas');
  traceCanvas.width = w; traceCanvas.height = h;
  var tcx = traceCanvas.getContext('2d');
  var traceData = tcx.createImageData(w, h);
  var td = traceData.data;

  var bg = hasA ? null : _detectBg(d, w, h);
  for (var y = 0; y < h; y++) {
    for (var x = 0; x < w; x++) {
      var pi = (y * w + x) * 4;
      var inside;
      if (hasA) {
        inside = d[pi + 3] > aT;
      } else {
        var dr = Math.abs(d[pi] - bg.r), dg = Math.abs(d[pi+1] - bg.g), db = Math.abs(d[pi+2] - bg.b);
        inside = (dr + dg + db) > 30;
      }
      var v = inside ? 255 : 0;
      td[pi] = v; td[pi+1] = v; td[pi+2] = v; td[pi+3] = 255;
    }
  }
  tcx.putImageData(traceData, 0, 0);

  // === Try ImageTracer (potrace-quality vectorization) ===
  if (typeof ImageTracer !== 'undefined') {
    try {
      var traceResult = ImageTracer.imagedataToTracedata(
        tcx.getImageData(0, 0, w, h),
        { ltres: 0.5, qtres: 0.5, pathomit: 15, numberofcolors: 2, colorsampling: 0, blurradius: 0 }
      );
      // Find the largest path (the main shape contour)
      var bestPath = null, bestArea = 0;
      if (traceResult && traceResult.layers) {
        for (var li = 0; li < traceResult.layers.length; li++) {
          var layer = traceResult.layers[li];
          for (var pi2 = 0; pi2 < layer.length; pi2++) {
            var path = layer[pi2];
            if (!path.segments || path.segments.length < 3) continue;
            // Convert segments to points and compute area
            var pts = [];
            for (var si = 0; si < path.segments.length; si++) {
              var seg = path.segments[si];
              pts.push({x: seg.x1 / w, y: seg.y1 / h});
              // For curves, add intermediate points
              if (seg.type === 'Q') {
                pts.push({x: seg.x2 / w, y: seg.y2 / h});
              } else if (seg.type === 'C') {
                pts.push({x: (seg.x1 + seg.x2) / 2 / w, y: (seg.y1 + seg.y2) / 2 / h});
                pts.push({x: seg.x2 / w, y: seg.y2 / h});
              }
            }
            var area = 0;
            for (var ai = 0, aj = pts.length - 1; ai < pts.length; aj = ai++) {
              area += (pts[aj].x + pts[ai].x) * (pts[aj].y - pts[ai].y);
            }
            area = Math.abs(area / 2);
            if (area > bestArea) { bestArea = area; bestPath = pts; }
          }
        }
      }
      if (bestPath && bestPath.length >= 3 && bestArea > 0.05) {
        // Clamp to 0-1
        for (var ci = 0; ci < bestPath.length; ci++) {
          bestPath[ci].x = Math.max(0, Math.min(1, bestPath[ci].x));
          bestPath[ci].y = Math.max(0, Math.min(1, bestPath[ci].y));
        }
        // Light simplification to remove redundant points while keeping shape
        var simplified = _dpClosed(bestPath, 0.003);
        if (simplified.length >= 3) return simplified;
      }
    } catch(e) { /* ImageTracer failed, fall through to Moore */ }
  }

  // === Fallback: Moore tracing + DP simplification ===
  var mw = w + 2, mh = h + 2;
  var mask = new Uint8Array(mw * mh);
  var filled = 0;
  for (var y2 = 0; y2 < h; y2++) {
    for (var x2 = 0; x2 < w; x2++) {
      var pi3 = (y2 * w + x2) * 4;
      if (td[pi3] > 128) { mask[(y2+1) * mw + (x2+1)] = 1; filled++; }
    }
  }
  if (filled < w * h * 0.05 || filled > w * h * 0.95) return _rect();
  mask = _morphClose(mask, mw, mh);
  mask = _largestComponent(mask, mw, mh);
  var boundary = _mooreTrace(mask, mw, mh);
  if (!boundary || boundary.length < 3) return _rect();

  var poly = [];
  for (var bi = 0; bi < boundary.length; bi++) {
    poly.push({
      x: Math.max(0, Math.min(1, (boundary[bi].x - 1) / w)),
      y: Math.max(0, Math.min(1, (boundary[bi].y - 1) / h))
    });
  }
  // Moderate DP simplification (tolerance = 0.5% of image diagonal)
  poly = _dpClosed(poly, 0.005);
  if (poly.length > 1) {
    var la = poly[poly.length-1], fi = poly[0];
    if (Math.abs(la.x-fi.x) < 0.005 && Math.abs(la.y-fi.y) < 0.005) poly.pop();
  }
  return poly.length >= 3 ? poly : _rect();
};

function _rect() { return [{x:0,y:0},{x:1,y:0},{x:1,y:1},{x:0,y:1}]; }

function _detectBg(d, w, h) {
  var s = Math.min(5, Math.floor(w/4), Math.floor(h/4));
  if (s < 1) s = 1;
  var sr=0,sg=0,sb=0,n=0;
  for (var cy=0;cy<2;cy++) for (var cx2=0;cx2<2;cx2++) for (var dy=0;dy<s;dy++) for (var dx=0;dx<s;dx++) {
    var x = cx2===0?dx:w-1-dx, y = cy===0?dy:h-1-dy;
    var pi = (y*w+x)*4;
    sr+=d[pi]; sg+=d[pi+1]; sb+=d[pi+2]; n++;
  }
  return {r:Math.round(sr/n), g:Math.round(sg/n), b:Math.round(sb/n)};
}

// ==================== MORPHOLOGY ====================

function _morphClose(mask, w, h) {
  var dl = new Uint8Array(w*h);
  for (var y=0;y<h;y++) for (var x=0;x<w;x++) {
    if (mask[y*w+x]) { dl[y*w+x]=1; continue; }
    for (var dy=-1;dy<=1;dy++) for (var dx=-1;dx<=1;dx++) {
      var nx=x+dx,ny=y+dy;
      if (nx>=0&&nx<w&&ny>=0&&ny<h&&mask[ny*w+nx]) { dl[y*w+x]=1; dy=2; break; }
    }
  }
  var er = new Uint8Array(w*h);
  for (var y=0;y<h;y++) for (var x=0;x<w;x++) {
    if (!dl[y*w+x]) continue;
    var keep=true;
    for (var dy=-1;dy<=1&&keep;dy++) for (var dx=-1;dx<=1&&keep;dx++) {
      var nx=x+dx,ny=y+dy;
      if (nx<0||nx>=w||ny<0||ny>=h||!dl[ny*w+nx]) keep=false;
    }
    if (keep) er[y*w+x]=1;
  }
  return er;
}

// Extra erosion pass to strip anti-aliasing fringe
function _morphErode(mask, w, h) {
  var er = new Uint8Array(w*h);
  for (var y=0;y<h;y++) for (var x=0;x<w;x++) {
    if (!mask[y*w+x]) continue;
    var keep=true;
    for (var dy=-1;dy<=1&&keep;dy++) for (var dx=-1;dx<=1&&keep;dx++) {
      var nx=x+dx,ny=y+dy;
      if (nx<0||nx>=w||ny<0||ny>=h||!mask[ny*w+nx]) keep=false;
    }
    if (keep) er[y*w+x]=1;
  }
  // If erosion removed too much (>80% lost), skip it
  var origCount=0,newCount=0;
  for (var i=0;i<w*h;i++) { if(mask[i])origCount++; if(er[i])newCount++; }
  return newCount < origCount*0.2 ? mask : er;
}

// ==================== LARGEST CONNECTED COMPONENT ====================

function _largestComponent(mask, w, h) {
  var labels = new Int32Array(w*h);
  var compSizes = {};
  var label = 0;

  for (var y=0;y<h;y++) for (var x=0;x<w;x++) {
    if (!mask[y*w+x] || labels[y*w+x]) continue;
    label++;
    var stack = [y*w+x];
    var size = 0;
    while (stack.length) {
      var idx = stack.pop();
      if (labels[idx]) continue;
      labels[idx] = label; size++;
      var cx2 = idx % w, cy2 = Math.floor(idx / w);
      for (var dy=-1;dy<=1;dy++) for (var dx=-1;dx<=1;dx++) {
        if (!dx && !dy) continue;
        var nx=cx2+dx, ny=cy2+dy;
        if (nx>=0&&nx<w&&ny>=0&&ny<h&&mask[ny*w+nx]&&!labels[ny*w+nx]) stack.push(ny*w+nx);
      }
    }
    compSizes[label] = size;
  }

  // Find largest
  var bestL=0, bestS=0;
  for (var l in compSizes) { if (compSizes[l]>bestS) { bestS=compSizes[l]; bestL=parseInt(l); } }

  // Keep only largest
  var out = new Uint8Array(w*h);
  for (var i=0;i<w*h;i++) { if (labels[i]===bestL) out[i]=1; }
  return out;
}

// ==================== MOORE NEIGHBOR TRACING ====================

function _mooreTrace(mask, w, h) {
  var sx=-1,sy=-1;
  for (var y=0;y<h&&sx<0;y++) for (var x=0;x<w;x++) { if (mask[y*w+x]) { sx=x;sy=y;break; } }
  if (sx<0) return null;
  var dx8=[1,1,0,-1,-1,-1,0,1], dy8=[0,1,1,1,0,-1,-1,-1];
  var out=[], cx=sx, cy=sy, bd=4, steps=0, max=w*h;
  do {
    out.push({x:cx,y:cy});
    var ss=(bd+1)%8, found=false;
    for (var i=0;i<8;i++) {
      var dd=(ss+i)%8, nx=cx+dx8[dd], ny=cy+dy8[dd];
      if (nx>=0&&nx<w&&ny>=0&&ny<h&&mask[ny*w+nx]) { bd=(dd+4)%8; cx=nx; cy=ny; found=true; break; }
    }
    if (!found) break;
    steps++;
  } while ((cx!==sx||cy!==sy)&&steps<max);
  return out.length>=3?out:null;
}

// ==================== DOUGLAS-PEUCKER ====================

function _dpClosed(pts, tol) {
  if (pts.length<=3) return pts.slice();
  var mD=0,mI=0;
  for (var i=1;i<pts.length;i++) { var d2=_d2(pts[0],pts[i]); if (d2>mD){mD=d2;mI=i;} }
  var a=_dpRun(pts.slice(0,mI+1),tol);
  var b=_dpRun(pts.slice(mI).concat([pts[0]]),tol);
  return a.slice(0,a.length-1).concat(b.slice(0,b.length-1));
}

function _dpRun(pts, tol) {
  if (pts.length<=2) return pts.slice();
  var mD=0,mI=0,a=pts[0],b=pts[pts.length-1];
  for (var i=1;i<pts.length-1;i++) { var d=_ptLine(pts[i],a,b); if (d>mD){mD=d;mI=i;} }
  if (mD>tol) {
    var l=_dpRun(pts.slice(0,mI+1),tol);
    var r=_dpRun(pts.slice(mI),tol);
    return l.slice(0,l.length-1).concat(r);
  }
  return [a,b];
}

function _ptLine(p,a,b) {
  var dx=b.x-a.x,dy=b.y-a.y,ls=dx*dx+dy*dy;
  if (ls<1e-10) return Math.sqrt(_d2(p,a));
  var t=Math.max(0,Math.min(1,((p.x-a.x)*dx+(p.y-a.y)*dy)/ls));
  var px=a.x+t*dx,py=a.y+t*dy;
  return Math.sqrt((p.x-px)*(p.x-px)+(p.y-py)*(p.y-py));
}

function _d2(a,b) { return (a.x-b.x)*(a.x-b.x)+(a.y-b.y)*(a.y-b.y); }

// ==================== POLYGON UTILITIES ====================

NE.polyBounds = function(p) {
  var x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity;
  for (var i=0;i<p.length;i++) {
    if (p[i].x<x0) x0=p[i].x; if (p[i].y<y0) y0=p[i].y;
    if (p[i].x>x1) x1=p[i].x; if (p[i].y>y1) y1=p[i].y;
  }
  return {x:x0,y:y0,w:x1-x0,h:y1-y0};
};

NE.polyArea = function(p) {
  var a=0;
  for (var i=0,j=p.length-1;i<p.length;j=i++) a+=(p[j].x+p[i].x)*(p[j].y-p[i].y);
  return Math.abs(a/2);
};

NE.polyRotate = function(p, deg) {
  if (!deg) return p.slice();
  var rad=deg*Math.PI/180, co=Math.cos(rad), si=Math.sin(rad);
  var cx2=0,cy2=0;
  for (var i=0;i<p.length;i++) { cx2+=p[i].x; cy2+=p[i].y; }
  cx2/=p.length; cy2/=p.length;
  var out=[];
  for (var i=0;i<p.length;i++) {
    var dx=p[i].x-cx2, dy=p[i].y-cy2;
    out.push({x:cx2+dx*co-dy*si, y:cy2+dx*si+dy*co});
  }
  var b=NE.polyBounds(out);
  for (var i=0;i<out.length;i++) { out[i].x-=b.x; out[i].y-=b.y; }
  return out;
};

NE.polyScale = function(norm, ww, hh) {
  var out=[];
  for (var i=0;i<norm.length;i++) out.push({x:norm[i].x*ww, y:norm[i].y*hh});
  return out;
};

// Mirror polygon horizontally
NE.polyMirror = function(p) {
  var b = NE.polyBounds(p);
  var out = [];
  for (var i=0;i<p.length;i++) out.push({x: b.x + b.w - (p[i].x - b.x), y: p[i].y});
  return out;
};

function _pip(px, py, poly) {
  var ins=false;
  for (var i=0,j=poly.length-1;i<poly.length;j=i++) {
    var xi=poly[i].x,yi=poly[i].y,xj=poly[j].x,yj=poly[j].y;
    if (((yi>py)!==(yj>py))&&(px<(xj-xi)*(py-yi)/(yj-yi)+xi)) ins=!ins;
  }
  return ins;
}

function _ptSeg(px,py,a,b) {
  var dx=b.x-a.x,dy=b.y-a.y,ls=dx*dx+dy*dy;
  if (ls<1e-10) return Math.sqrt((px-a.x)*(px-a.x)+(py-a.y)*(py-a.y));
  var t=Math.max(0,Math.min(1,((px-a.x)*dx+(py-a.y)*dy)/ls));
  var qx=a.x+t*dx,qy=a.y+t*dy;
  return Math.sqrt((px-qx)*(px-qx)+(py-qy)*(py-qy));
}

// ==================== ROTATION HELPERS ====================

// Generate rotation angles: every `step` degrees from 0 to 359
NE.makeRotations = function(step) {
  step = step || 15;
  var r = [];
  for (var a = 0; a < 360; a += step) r.push(a);
  return r;
};

// Filter allowed rotations based on grain line constraint
// grainAngle: the required grain direction (degrees, 0=vertical)
// napDir: 'bi' (allow 180 flip) or 'uni' (strict direction)
NE.grainRotations = function(grainAngle, napDir) {
  var rots = [grainAngle % 360];
  if (napDir !== 'uni') rots.push((grainAngle + 180) % 360);
  return rots;
};

// ==================== RASTERIZATION ====================

NE.rasterize = function(polyMm, cellSize) {
  var b = NE.polyBounds(polyMm);
  var gw = Math.ceil((b.w + 0.5) / cellSize) + 1;
  var gh = Math.ceil((b.h + 0.5) / cellSize) + 1;
  // Safety: cap raster size to avoid memory issues
  if (gw * gh > 500000) {
    cellSize = Math.sqrt(b.w * b.h / 200000) || cellSize;
    gw = Math.ceil((b.w + 0.5) / cellSize) + 1;
    gh = Math.ceil((b.h + 0.5) / cellSize) + 1;
  }
  var cells = [];
  // Scanline fill: for each row, find X intersections with polygon edges
  var n = polyMm.length;
  for (var gy = 0; gy < gh; gy++) {
    var my = b.y + (gy + 0.5) * cellSize;
    var xInts = [];
    for (var i = 0, j = n - 1; i < n; j = i++) {
      var yi = polyMm[i].y, yj = polyMm[j].y;
      if ((yi > my) !== (yj > my)) {
        xInts.push(polyMm[j].x + (my - yj) / (yi - yj) * (polyMm[i].x - polyMm[j].x));
      }
    }
    xInts.sort(function(a, b2) { return a - b2; });
    for (var k = 0; k < xInts.length - 1; k += 2) {
      var xStart = Math.max(0, Math.ceil((xInts[k] - b.x - 0.5 * cellSize) / cellSize));
      var xEnd = Math.min(gw - 1, Math.floor((xInts[k + 1] - b.x - 0.5 * cellSize) / cellSize));
      for (var gx = xStart; gx <= xEnd; gx++) cells.push({x: gx, y: gy});
    }
  }
  return cells;
};

function _addGap(core, gCX, gCY) {
  if (gCX<=0&&gCY<=0) return core.slice();
  var set={};
  for (var i=0;i<core.length;i++) set[core[i].x+','+core[i].y]=1;
  var out=core.slice();
  for (var i=0;i<core.length;i++) {
    for (var dy=-gCY;dy<=gCY;dy++) for (var dx=-gCX;dx<=gCX;dx++) {
      if (!dx&&!dy) continue;
      var k=(core[i].x+dx)+','+(core[i].y+dy);
      if (!set[k]) { set[k]=1; out.push({x:core[i].x+dx, y:core[i].y+dy}); }
    }
  }
  return out;
}

function _cellBounds(cells) {
  var x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity;
  for (var i=0;i<cells.length;i++) {
    if (cells[i].x<x0) x0=cells[i].x; if (cells[i].y<y0) y0=cells[i].y;
    if (cells[i].x>x1) x1=cells[i].x; if (cells[i].y>y1) y1=cells[i].y;
  }
  return {x0:x0,y0:y0,x1:x1,y1:y1};
}

// ==================== PLACEMENT GRID ====================

function Grid(cols, rows) {
  this.cols=cols; this.rows=rows;
  this.g=new Uint8Array(cols*rows);
  this.sky=new Float64Array(cols);
}

Grid.prototype.canPlace=function(cells,ox,oy){
  for (var i=0;i<cells.length;i++) {
    var gx=cells[i].x+ox, gy=cells[i].y+oy;
    if (gx<0||gx>=this.cols||gy<0||gy>=this.rows) return false;
    if (this.g[gy*this.cols+gx]) return false;
  }
  return true;
};

Grid.prototype.mark=function(cells,ox,oy){
  for (var i=0;i<cells.length;i++) {
    var gx=cells[i].x+ox, gy=cells[i].y+oy;
    if (gx>=0&&gx<this.cols&&gy>=0&&gy<this.rows) {
      this.g[gy*this.cols+gx]=1;
      if (gy+1>this.sky[gx]) this.sky[gx]=gy+1;
    }
  }
};

Grid.prototype.findPos=function(core,coreB){
  if (!core.length) return null;
  var cmr={};
  for (var i=0;i<core.length;i++) {
    var cx2=core[i].x;
    if (cmr[cx2]===undefined||core[i].y<cmr[cx2]) cmr[cx2]=core[i].y;
  }
  var maxOX=this.cols-coreB.x1-1, maxOY=this.rows-coreB.y1-1;
  if (maxOX<0||maxOY<0) return null;

  var bx=-1,by=-1,bs=Infinity;
  for (var ox=0;ox<=maxOX;ox++) {
    var minOY=0;
    for (var c in cmr) {
      var sc2=ox+parseInt(c);
      if (sc2>=0&&sc2<this.cols) { var nd=this.sky[sc2]-cmr[c]; if (nd>minOY) minOY=nd; }
    }
    minOY=Math.max(0,Math.ceil(minOY));
    for (var oy=minOY;oy<=maxOY;oy++) {
      if (this.canPlace(core,ox,oy)) {
        var sc3=oy*10000+ox;
        if (sc3<bs) { bs=sc3; bx=ox; by=oy; }
        break;
      }
    }
  }
  return bx>=0?{ox:bx,oy:by}:null;
};

// ==================== ITEMS & PRECOMPUTE ====================

NE.buildItems = function(motifs, dpi, opts) {
  opts = opts || {};
  var items=[];
  motifs.forEach(function(m) {
    var wMm = m.overW!=null ? m.overW : (m.widthPx/dpi)*25.4;
    var hMm = m.overH!=null ? m.overH : (m.heightPx/dpi)*25.4;
    var pn = m.polygon || _rect();
    var pm = NE.polyScale(pn, wMm, hMm);
    for (var i=0;i<m.qty;i++) {
      items.push({ mid:m.id, wMm:wMm, hMm:hMm, polyNorm:pn, polyMm:pm,
        pivotable:m.pivotable, area:NE.polyArea(pm),
        // Patronage constraints (optional)
        grainAngle: m.grainAngle || null,
        allowMirror: m.allowMirror || false,
        napDir: opts.napDir || 'bi'
      });
    }
  });
  return items;
};

function _precompute(items, cs, gCX, gCY, rots) {
  var cache={};
  items.forEach(function(it,idx) {
    cache[idx]={};
    // Determine allowed rotations for this item
    var allowed;
    if (it.grainAngle !== null) {
      allowed = NE.grainRotations(it.grainAngle, it.napDir);
    } else {
      allowed = it.pivotable ? rots : [0];
    }
    allowed.forEach(function(ang) {
      var poly = ang===0 ? it.polyMm : NE.polyRotate(it.polyMm, ang);
      var core = NE.rasterize(poly, cs);
      if (!core.length) return;
      var gap = _addGap(core, gCX, gCY);
      var cb = _cellBounds(core);
      cache[idx][ang] = { core:core, gap:gap, poly:poly, bounds:NE.polyBounds(poly), cb:cb };
    });
    // Mirror variants if allowed
    if (it.allowMirror) {
      var mirPoly = NE.polyMirror(it.polyMm);
      allowed.forEach(function(ang) {
        var key = 'm' + ang;
        var poly = ang===0 ? mirPoly : NE.polyRotate(mirPoly, ang);
        var core = NE.rasterize(poly, cs);
        if (!core.length) return;
        var gap = _addGap(core, gCX, gCY);
        var cb = _cellBounds(core);
        cache[idx][key] = { core:core, gap:gap, poly:poly, bounds:NE.polyBounds(poly), cb:cb };
      });
    }
  });
  return cache;
}

function _getAllAngles(cache, idx) {
  var angs = [];
  for (var k in cache[idx]) angs.push(k);
  return angs;
}

// ==================== SMART HEIGHT ESTIMATION ====================
function _estimateMaxH(items, uW) {
  // For roll mode: estimate a reasonable max height to avoid allocating huge grids
  var totalArea = 0, maxDim = 0, totalH = 0;
  for (var i = 0; i < items.length; i++) {
    totalArea += items[i].area;
    maxDim = Math.max(maxDim, items[i].wMm, items[i].hMm);
    totalH += Math.max(items[i].wMm, items[i].hMm);
  }
  // Height = stacked height (worst case) capped by area estimate with 3x safety
  var areaEstimate = uW > 0 ? (totalArea / uW) * 4 : totalH;
  var h = Math.max(areaEstimate, totalH, maxDim * 2, 500);
  // Cap to prevent memory issues (max ~10 million cells)
  var maxCells = 8000000;
  return Math.min(h, maxCells / Math.max(1, Math.ceil(uW)));
}

// ==================== SINGLE-PASS NESTING ====================

NE.nestQuick = function(items, sheetW, sheetH, opts) {
  opts=opts||{};
  var cs=opts.cellSize||1;
  var mg=opts.margins||{l:0,r:0,t:0,b:0};
  var isS=opts.isSheet!==false;
  var uW=sheetW-mg.l-mg.r;
  var uH=isS?(sheetH-mg.t-mg.b):_estimateMaxH(items, uW);
  if (uW<=0||uH<=0) return {placed:0,total:items.length,placements:[],height:0,fill:0};

  var gCX=Math.ceil((opts.gapX||0)/cs), gCY=Math.ceil((opts.gapY||0)/cs);
  var rots = opts.rotations || NE.makeRotations(opts.rotationStep || 15);
  var cache=_precompute(items,cs,gCX,gCY,rots);
  var gc=Math.ceil(uW/cs), gr=Math.ceil(uH/cs);
  var grid=new Grid(gc,gr);

  var order=[];
  for (var i=0;i<items.length;i++) order.push(i);
  order.sort(function(a,b){return items[b].area-items[a].area;});

  var placements=[], maxH=0, usedA=0;
  for (var oi=0;oi<order.length;oi++) {
    var idx=order[oi], it=items[idx];
    var bp=null,ba=0,bsc=Infinity;
    var angs=_getAllAngles(cache,idx);
    for (var ai=0;ai<angs.length;ai++) {
      var c=cache[idx][angs[ai]]; if (!c) continue;
      var pos=grid.findPos(c.core,c.cb);
      if (pos) { var sc2=pos.oy*10000+pos.ox; if (sc2<bsc){bsc=sc2;bp=pos;ba=angs[ai];} }
    }
    if (bp) {
      var c2=cache[idx][ba];
      grid.mark(c2.gap,bp.ox,bp.oy);
      var px=mg.l+bp.ox*cs, py=mg.t+bp.oy*cs;
      var bb=c2.bounds;
      placements.push({
        motifId:it.mid, x:px, y:py, w:bb.w, h:bb.h,
        rotated:ba!==0&&ba!=='0', rotation:ba,
        polygon:c2.poly.map(function(p){return{x:p.x+px,y:p.y+py};})
      });
      var ph=py+bb.h; if (ph>maxH) maxH=ph;
      usedA+=it.area;
    }
  }

  var totA=uW*(isS?uH:Math.max(1,maxH-mg.t));
  return {placed:placements.length, total:items.length, placements:placements,
    height:maxH, fill:totA>0?(usedA/totA*100):0, usedArea:usedA};
};

// ==================== GA + SIMULATED ANNEALING NESTING ====================

NE.nestGA = function(items, sheetW, sheetH, opts) {
  opts=opts||{};
  var cs=opts.cellSize||1;
  var mg=opts.margins||{l:0,r:0,t:0,b:0};
  var isS=opts.isSheet!==false;
  var popSz=opts.population||24;
  var gens=opts.generations||30;
  var mutR=opts.mutationRate||0.3;
  var saIters=opts.saIterations||300;
  var onProg=opts.onProgress||function(){};

  var uW=sheetW-mg.l-mg.r;
  var uH=isS?(sheetH-mg.t-mg.b):_estimateMaxH(items, uW);
  if (uW<=0) return Promise.resolve(null);

  var gCX=Math.ceil((opts.gapX||0)/cs), gCY=Math.ceil((opts.gapY||0)/cs);
  var rots = opts.rotations || NE.makeRotations(opts.rotationStep || 15);
  var cache=_precompute(items,cs,gCX,gCY,rots);
  var gc=Math.ceil(uW/cs), gr=Math.ceil(uH/cs);
  var n=items.length;
  if (!n) return Promise.resolve(null);

  // Adaptive params
  if (n>40) { popSz=Math.min(popSz,12); gens=Math.min(gens,15); }
  else if (n>20) { popSz=Math.min(popSz,16); gens=Math.min(gens,20); }

  // Build angle lists per item from cache
  var itemAngles = [];
  for (var i=0;i<n;i++) itemAngles.push(_getAllAngles(cache,i));

  function randInd() {
    var o=[]; for (var i=0;i<n;i++) o.push(i);
    for (var i=n-1;i>0;i--) { var j=Math.floor(Math.random()*(i+1)); var t=o[i];o[i]=o[j];o[j]=t; }
    var r=[]; for (var i=0;i<n;i++) {
      var ia=itemAngles[i]; r.push(ia[Math.floor(Math.random()*ia.length)]);
    }
    return {order:o,rots:r,fit:Infinity,placed:0,maxH:0};
  }
  function seedInd() {
    var o=[]; for (var i=0;i<n;i++) o.push(i);
    o.sort(function(a,b){return items[b].area-items[a].area;});
    var r=[]; for (var i=0;i<n;i++) r.push(itemAngles[i][0]);
    return {order:o,rots:r,fit:Infinity,placed:0,maxH:0};
  }

  function evaluate(ind) {
    var grid=new Grid(gc,gr);
    var placed=0,maxH=0;
    for (var oi=0;oi<ind.order.length;oi++) {
      var idx=ind.order[oi], ang=ind.rots[idx];
      var c=cache[idx][ang]; if (!c) { c=cache[idx][itemAngles[idx][0]]; }
      if (!c) continue;
      var pos=grid.findPos(c.core,c.cb);
      if (pos) {
        grid.mark(c.gap,pos.ox,pos.oy);
        var ph=(pos.oy+c.cb.y1+1)*cs;
        if (ph>maxH) maxH=ph;
        placed++;
      }
    }
    ind.fit=maxH+(n-placed)*99999;
    ind.placed=placed; ind.maxH=maxH;
  }

  function tournament(pop) {
    var a=pop[Math.floor(Math.random()*pop.length)];
    var b=pop[Math.floor(Math.random()*pop.length)];
    var c=pop[Math.floor(Math.random()*pop.length)];
    return a.fit<=b.fit?(a.fit<=c.fit?a:c):(b.fit<=c.fit?b:c);
  }

  function oxCross(p1,p2) {
    var len=p1.length, a=Math.floor(Math.random()*len), b=Math.floor(Math.random()*len);
    if (a>b){var t=a;a=b;b=t;}
    var ch=new Array(len).fill(-1), used={};
    for (var i=a;i<=b;i++){ch[i]=p1[i];used[p1[i]]=1;}
    var ci=(b+1)%len;
    for (var i=0;i<len;i++){var pi=(b+1+i)%len;if(!used[p2[pi]]){ch[ci]=p2[pi];ci=(ci+1)%len;}}
    return ch;
  }

  function cloneInd(ind) {
    return {order:ind.order.slice(), rots:ind.rots.slice(), fit:ind.fit, placed:ind.placed, maxH:ind.maxH};
  }

  return new Promise(function(resolve) {
    var pop=[];
    pop.push(seedInd());
    var seed2={order:[],rots:[],fit:Infinity,placed:0,maxH:0};
    for (var i=0;i<n;i++) seed2.order.push(i);
    seed2.order.sort(function(a,b){return Math.max(items[b].wMm,items[b].hMm)-Math.max(items[a].wMm,items[a].hMm);});
    for (var i=0;i<n;i++) seed2.rots.push(itemAngles[i][0]);
    pop.push(seed2);
    while (pop.length<popSz) pop.push(randInd());
    for (var i=0;i<pop.length;i++) evaluate(pop[i]);

    var gen=0;
    var totalSteps = gens + 1; // +1 for SA phase

    function gaStep() {
      pop.sort(function(a,b){return a.fit-b.fit;});
      var best=pop[0];
      var totArea=items.reduce(function(s,it){return s+it.area;},0);
      var usH=isS?uH:Math.max(1,best.maxH);
      onProg({
        generation:gen, totalGenerations:totalSteps,
        placed:best.placed, total:n, height:best.maxH,
        fill:uW*usH>0?(totArea/(uW*usH)*100):0,
        phase:'GA'
      });

      if (gen>=gens) {
        // ===== SIMULATED ANNEALING PHASE =====
        onProg({ generation:gens, totalGenerations:totalSteps, placed:best.placed, total:n, height:best.maxH,
          fill:uW*usH>0?(totArea/(uW*usH)*100):0, phase:'SA' });
        var saBest = cloneInd(best);
        var saCur = cloneInd(best);
        var temp = Math.max(1, saBest.maxH * 0.15);
        var cool = Math.pow(0.01/Math.max(0.01,temp), 1/Math.max(1,saIters));

        for (var si=0; si<saIters; si++) {
          var neighbor = cloneInd(saCur);
          var move = Math.random();
          if (move < 0.45) {
            // Swap two in order
            var a2=Math.floor(Math.random()*n), b2=Math.floor(Math.random()*n);
            var tmp=neighbor.order[a2]; neighbor.order[a2]=neighbor.order[b2]; neighbor.order[b2]=tmp;
          } else if (move < 0.75) {
            // Change one rotation
            var ri=Math.floor(Math.random()*n);
            var ia2=itemAngles[ri];
            neighbor.rots[ri] = ia2[Math.floor(Math.random()*ia2.length)];
          } else {
            // Reverse a subsequence (2-opt)
            var a3=Math.floor(Math.random()*n), b3=Math.floor(Math.random()*n);
            if (a3>b3){var t3=a3;a3=b3;b3=t3;}
            while(a3<b3){var t4=neighbor.order[a3];neighbor.order[a3]=neighbor.order[b3];neighbor.order[b3]=t4;a3++;b3--;}
          }
          evaluate(neighbor);
          var delta = neighbor.fit - saCur.fit;
          if (delta < 0 || Math.random() < Math.exp(-delta / temp)) {
            saCur = neighbor;
            if (saCur.fit < saBest.fit) saBest = cloneInd(saCur);
          }
          temp *= cool;
        }

        var result = _buildResult(saBest, items, cache, itemAngles, gc, gr, cs, mg, isS, uW, uH);
        onProg({ generation:totalSteps, totalGenerations:totalSteps, placed:result.placed, total:n,
          height:result.height, fill:result.fill, phase:'done' });
        resolve(result);
        return;
      }

      var next=[];
      for (var e=0;e<Math.min(2,pop.length);e++) next.push(cloneInd(pop[e]));

      while (next.length<popSz) {
        var pa=tournament(pop), pb=tournament(pop);
        var co=oxCross(pa.order,pb.order);
        var cr=[];
        for (var i=0;i<n;i++) cr.push(Math.random()<0.5?pa.rots[i]:pb.rots[i]);
        if (Math.random()<mutR) { var a2=Math.floor(Math.random()*n),b2=Math.floor(Math.random()*n);var t2=co[a2];co[a2]=co[b2];co[b2]=t2; }
        if (Math.random()<mutR) { var mi=Math.floor(Math.random()*n); var ia3=itemAngles[mi]; cr[mi]=ia3[Math.floor(Math.random()*ia3.length)]; }
        var child={order:co,rots:cr,fit:Infinity,placed:0,maxH:0};
        evaluate(child);
        next.push(child);
      }

      pop=next; gen++;
      setTimeout(gaStep,0);
    }
    gaStep();
  });
};

function _buildResult(ind, items, cache, itemAngles, gc, gr, cs, mg, isS, uW, uH) {
  var grid=new Grid(gc,gr);
  var placements=[], maxH=0, usedA=0;

  for (var oi=0;oi<ind.order.length;oi++) {
    var idx=ind.order[oi], ang=ind.rots[idx];
    var c=cache[idx][ang]; if (!c) c=cache[idx][itemAngles[idx][0]];
    if (!c) continue;
    var pos=grid.findPos(c.core,c.cb);
    if (pos) {
      grid.mark(c.gap,pos.ox,pos.oy);
      var px=mg.l+pos.ox*cs, py=mg.t+pos.oy*cs;
      var bb=c.bounds;
      placements.push({
        motifId:items[idx].mid, x:px, y:py, w:bb.w, h:bb.h,
        rotated:ang!==0&&ang!=='0', rotation:ang,
        polygon:c.poly.map(function(p){return{x:p.x+px,y:p.y+py};})
      });
      var ph=py+bb.h; if (ph>maxH) maxH=ph;
      usedA+=items[idx].area;
    }
  }

  var totA=uW*(isS?uH:Math.max(1,maxH-mg.t));
  return {
    placements:placements, placed:placements.length, total:items.length,
    height:maxH, fill:totA>0?(usedA/totA*100):0, usedArea:usedA
  };
}

// ==================== EXPORT ====================
global.NestEngine = NE;
})(window);
